import type { ImportPaygapPersonnelDto } from "@green0meter/api-client";

import { dateFieldToNumber } from "./useEmployeeDataMapper";
import type { EmployeeData } from "./usePaygapData";

/**
 * Import utilities for transforming and batching employee data.
 */

const BATCH_SIZE = 100;

type PersonnelItem = ImportPaygapPersonnelDto["personnel"][number];

/**
 * Map an EmployeeData object to the API personnel format
 */
export const mapEmployeeToPersonnel = (emp: EmployeeData): PersonnelItem => {
  return {
    key: emp.key,
    age: dateFieldToNumber(emp.age),
    gender:
      typeof emp.gender === "number"
        ? emp.gender
        : parseInt(String(emp.gender)) || 0,
    yearsOfService: dateFieldToNumber(emp.yearsOfService),
    educationAndTraining: emp.educationAndTraining,
    employeeScarcity:
      typeof emp.employeeScarcity === "number"
        ? emp.employeeScarcity
        : parseInt(String(emp.employeeScarcity)) || 0,
    employeeEvaluation:
      typeof emp.employeeEvaluation === "number"
        ? emp.employeeEvaluation
        : parseInt(String(emp.employeeEvaluation)) || 0,
    category: emp.category,
    function: emp.function,
    levelOfRequirements:
      emp.levelOfRequirements != null ? String(emp.levelOfRequirements) : undefined,
    levelOfRequirements2:
      emp.levelOfRequirements2 != null
        ? String(emp.levelOfRequirements2)
        : undefined,
    levelOfRequirements3:
      emp.levelOfRequirements3 != null
        ? String(emp.levelOfRequirements3)
        : undefined,
    professionalPosition: String(emp.professionalPosition ?? ""),
    activityRatePercent:
      typeof emp.activityRatePercent === "number"
        ? emp.activityRatePercent
        : parseInt(String(emp.activityRatePercent)) || 0,
    paidHours:
      typeof emp.paidHours === "number"
        ? emp.paidHours
        : parseInt(String(emp.paidHours)) || 0,
    basicWage:
      typeof emp.basicWage === "number"
        ? emp.basicWage
        : parseInt(String(emp.basicWage)) || 0,
    wageCompensationForVacation:
      typeof emp.wageCompensationForVacation === "number"
        ? emp.wageCompensationForVacation
        : parseInt(String(emp.wageCompensationForVacation)) || 0,
    wageCompensationDoctorFamilyLeave:
      typeof emp.wageCompensationDoctorFamilyLeave === "number"
        ? emp.wageCompensationDoctorFamilyLeave
        : parseInt(String(emp.wageCompensationDoctorFamilyLeave)) || 0,
    sickLeaveIncomeCompensation:
      typeof emp.sickLeaveIncomeCompensation === "number"
        ? emp.sickLeaveIncomeCompensation
        : parseInt(String(emp.sickLeaveIncomeCompensation)) || 0,
    allowances:
      typeof emp.allowances === "number"
        ? emp.allowances
        : parseInt(String(emp.allowances)) || 0,
    overtimeWage:
      typeof emp.overtimeWage === "number"
        ? emp.overtimeWage
        : parseInt(String(emp.overtimeWage)) || 0,
    overtimeAllowance:
      typeof emp.overtimeAllowance === "number"
        ? emp.overtimeAllowance
        : parseInt(String(emp.overtimeAllowance)) || 0,
    riskAllowance:
      typeof emp.riskAllowance === "number"
        ? emp.riskAllowance
        : parseInt(String(emp.riskAllowance)) || 0,
    standbyDuty:
      typeof emp.standbyDuty === "number"
        ? emp.standbyDuty
        : parseInt(String(emp.standbyDuty)) || 0,
    thirteenthMonthlyWage:
      typeof emp.thirteenthMonthlyWage === "number"
        ? emp.thirteenthMonthlyWage
        : parseInt(String(emp.thirteenthMonthlyWage)) || 0,
    monthlyBonus:
      typeof emp.monthlyBonus === "number"
        ? emp.monthlyBonus
        : parseInt(String(emp.monthlyBonus)) || 0,
    standardBonus:
      typeof emp.standardBonus === "number"
        ? emp.standardBonus
        : parseInt(String(emp.standardBonus)) || 0,
    specialBonus:
      typeof emp.specialBonus === "number"
        ? emp.specialBonus
        : parseInt(String(emp.specialBonus)) || 0,
    fiftyFivePlusBonus:
      typeof emp.fiftyFivePlusBonus === "number"
        ? emp.fiftyFivePlusBonus
        : parseInt(String(emp.fiftyFivePlusBonus)) || 0,
    retirementBenefit:
      typeof emp.retirementBenefit === "number"
        ? emp.retirementBenefit
        : parseInt(String(emp.retirementBenefit)) || 0,
    severancePay:
      typeof emp.severancePay === "number"
        ? emp.severancePay
        : parseInt(String(emp.severancePay)) || 0,
    specialPayments:
      typeof emp.specialPayments === "number"
        ? emp.specialPayments
        : parseInt(String(emp.specialPayments)) || 0,
    supplementaryPensionInsurance:
      typeof emp.supplementaryPensionInsurance === "number"
        ? emp.supplementaryPensionInsurance
        : parseInt(String(emp.supplementaryPensionInsurance)) || 0,
    lifeInsurance:
      typeof emp.lifeInsurance === "number"
        ? emp.lifeInsurance
        : parseInt(String(emp.lifeInsurance)) || 0,
    mealVouchers:
      typeof emp.mealVouchers === "number"
        ? emp.mealVouchers
        : parseInt(String(emp.mealVouchers)) || 0,
    cafeteria:
      typeof emp.cafeteria === "number"
        ? emp.cafeteria
        : parseInt(String(emp.cafeteria)) || 0,
    carAllowance:
      typeof emp.carAllowance === "number"
        ? emp.carAllowance
        : parseInt(String(emp.carAllowance)) || 0,
    employeeContribution:
      typeof emp.employeeContribution === "number"
        ? emp.employeeContribution
        : parseInt(String(emp.employeeContribution)) || 0,
    usualWeeklyWorkingHours:
      typeof emp.usualWeeklyWorkingHours === "number"
        ? emp.usualWeeklyWorkingHours
        : parseInt(String(emp.usualWeeklyWorkingHours)) || 0,
    usualAnnualWorkingHours:
      typeof emp.usualAnnualWorkingHours === "number"
        ? emp.usualAnnualWorkingHours
        : parseInt(String(emp.usualAnnualWorkingHours)) || 0,
    statisticalPopulation:
      typeof emp.statisticalPopulation === "number"
        ? emp.statisticalPopulation
        : parseInt(String(emp.statisticalPopulation)) || 0,
    remarks: String(emp.remarks ?? ""),
    additionalInformation01: String(emp.additionalInformation01 ?? ""),
    additionalInformation02: String(emp.additionalInformation02 ?? ""),
    additionalInformation03: String(emp.additionalInformation03 ?? ""),
    additionalInformation04: String(emp.additionalInformation04 ?? ""),
    additionalInformation05: String(emp.additionalInformation05 ?? ""),
  } as PersonnelItem;
};

/**
 * Create batches from employee data
 */
export const createBatches = <T>(data: T[]): T[][] => {
  const batches: T[][] = [];

  for (let i = 0; i < data.length; i += BATCH_SIZE) {
    batches.push(data.slice(i, i + BATCH_SIZE));
  }

  return batches;
};

/**
 * Import employee data in batches
 */
export const importDataInBatches = async (
  employeeData: EmployeeData[],
  importPersonnel: (request: ImportPaygapPersonnelDto) => Promise<void>,
): Promise<void> => {
  const personnel = employeeData.map(mapEmployeeToPersonnel);
  const batches = createBatches(personnel);

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const isLastBatch = i === batches.length - 1;

    const importRequest: ImportPaygapPersonnelDto = {
      personnel: batch as unknown as ImportPaygapPersonnelDto["personnel"],
      overwrite: i === 0,
    };

    await importPersonnel(importRequest);

    if (!isLastBatch) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }
};
